<template>
  <div data-testid="add-metric-side-sheet">
    <v-navigation-drawer
      :value="isOpen"
      temporary
      fixed
      right
      width="502"
      @input="handleDrawerInput"
    >
      <DrawerToolbar
        admin
        :title="drawerTitle"
        :show-back-button="true"
        :show-close-button="false"
        @back="handleBack"
      />

      <div v-if="view === 'picker'" class="sheet-body">
        <Typography variant="h-5">Choose a metric type</Typography>

        <v-radio-group
          :value="componentType"
          hide-details
          class="type-options"
          @change="selectType"
        >
          <v-sheet
            v-for="type in componentTypes"
            :key="type.value"
            outlined
            rounded="lg"
            class="type-option"
            :data-testid="`metric-type-${type.value}`"
            @click="selectType(type.value)"
          >
            <v-radio :label="type.label" :value="type.value" color="brandPrimary" />

            <div class="type-option__preview">
              <template v-if="type.value === 'BIG_NUMBER'">
                <Typography variant="h-5" class="text--disabled">1,010</Typography>
                <Typography variant="caption-2" class="text--disabled">metric label</Typography>
              </template>

              <template v-else-if="type.value === 'SMALL_NUMBER'">
                <Typography variant="body-1" class="text--disabled">1,010</Typography>
                <Typography variant="caption-2" class="text--disabled">metric label</Typography>
              </template>

              <v-progress-linear
                v-else-if="type.value === 'PROGRESS_BAR'"
                :value="70"
                height="10"
                rounded
                color="grey"
                background-color="grey lighten-2"
              />

              <div v-else class="type-option__status">
                <Icon icon="mdi-check-circle-outline" small color="grey" />
                <Typography variant="body-2" class="text--disabled">Requirement met</Typography>
              </div>
            </div>
          </v-sheet>
        </v-radio-group>
      </div>

      <div v-else class="sheet-body">
        <Typography variant="h-5">Configure</Typography>

        <Alert
          v-if="metricOptionsError"
          variant="error"
          message="Could not load metric fields. Try again later."
          data-testid="metric-options-error"
        />
        <Alert
          v-else-if="metricOptionsLoaded && !metricOptions.length"
          variant="info"
          :message="emptyOptionsMessage"
          data-testid="metric-options-empty"
        />

        <v-form ref="metricForm" class="fields">
          <NumberMetricForm
            v-if="isNumberType"
            v-model="form"
            :metric-options="metricOptions"
            :inform-program-id="informProgramId"
            @update:valid="formValid = $event"
          />
          <ProgressBarForm
            v-else-if="componentType === 'PROGRESS_BAR'"
            v-model="form"
            :metric-options="metricOptions"
            :inform-program-id="informProgramId"
            @update:valid="formValid = $event"
          />
          <StatusForm
            v-else
            v-model="form"
            :metric-options="metricOptions"
            :inform-program-id="informProgramId"
            @update:valid="formValid = $event"
          />

          <v-divider />

          <div class="visibility">
            <v-switch
              v-model="conditionalVisibility"
              label="Conditional visibility"
              hint="Show this metric only when a condition is met"
              persistent-hint
              hide-details="auto"
              color="brandPrimary"
              data-testid="conditional-visibility-toggle"
            />
            <MetricSelect
              v-if="conditionalVisibility"
              v-model="visibilityMetricAttribute"
              label="Visibility Metric*"
              required
              hint="This metric will appear when this value is true"
              :items="metricOptions"
              class="visibility__field"
              data-testid="metric-field-visibilityMetricAttribute"
            />
          </div>
        </v-form>

        <div class="actions">
          <Button text data-testid="cancel-metric-btn" @click="requestClose()">Cancel</Button>
          <Button data-testid="submit-metric-btn" @click="handleSubmit">
            {{ submitButtonText }}
          </Button>
        </div>
      </div>
    </v-navigation-drawer>
    <v-overlay :value="value" />
    <ErrorDialogV2
      v-model="showDiscardConfirm"
      title="Leave without saving?"
      message="The metric details in this panel won't be saved."
      confirm-button-text="Discard"
      cancel-button-text="Keep editing"
      show-cancel-button
      persistent
      @confirm="close"
      @cancel="handleKeepEditing"
    />
  </div>
</template>

<script>
import DrawerToolbar from "@/components/generic/DrawerToolbar.vue";
import Typography from "@/components/generic/Typography.vue";
import Button from "@/components/generic/Button.vue";
import Icon from "@/components/generic/Icon.vue";
import Alert from "@/components/generic/Alert.vue";
import ErrorDialogV2 from "@/components/ErrorDialogV2.vue";
import MetricSelect from "./MetricSelect.vue";
import NumberMetricForm from "./NumberMetricForm.vue";
import ProgressBarForm from "./ProgressBarForm.vue";
import StatusForm from "./StatusForm.vue";
import {
  METRIC_COMPONENT_TYPES,
  emptyMetricColumn,
  metricOptionsEmptyMessage,
} from "./metricOptions";

// The metric's own attributes, minus the type (a type pick on its own isn't data)
// and minus the ids the API owns, which never come from the form.
const METRIC_DATA_KEYS = Object.keys(emptyMetricColumn()).filter((key) => key !== "componentType");

export default {
  name: "AddMetricSideSheet",
  components: {
    DrawerToolbar,
    Typography,
    Button,
    Icon,
    Alert,
    ErrorDialogV2,
    MetricSelect,
    NumberMetricForm,
    ProgressBarForm,
    StatusForm,
  },
  props: {
    value: { type: Boolean, default: false },
    // Pre-populates the form when editing a configured column. `null` starts a
    // fresh Add-Metric flow.
    column: { type: Object, default: null },
    metricOptions: { type: Array, default: () => [] },
    informProgramId: { type: String, default: null },
    metricOptionsError: { type: Boolean, default: false },
    metricOptionsLoaded: { type: Boolean, default: false },
    metricOptionsReason: { type: String, default: null },
  },
  emits: ["input", "submit"],
  data() {
    return {
      view: "picker",
      componentTypes: METRIC_COMPONENT_TYPES,
      form: emptyMetricColumn(),
      formValid: false,
      conditionalVisibility: false,
      visibilityMetricAttribute: null,
      isOpen: this.value,
      showDiscardConfirm: false,
    };
  },
  computed: {
    componentType() {
      return this.form.componentType;
    },
    isEditing() {
      return !!this.column;
    },
    drawerTitle() {
      const verb = this.isEditing ? "Edit" : "Add";
      if (this.view === "picker") return `${verb} Metric`;
      const type = METRIC_COMPONENT_TYPES.find((t) => t.value === this.componentType);
      return `${verb} ${type.label}`;
    },
    submitButtonText() {
      return this.isEditing ? "Update Metric" : "Add Metric";
    },
    isNumberType() {
      return this.componentType === "BIG_NUMBER" || this.componentType === "SMALL_NUMBER";
    },
    emptyOptionsMessage() {
      return metricOptionsEmptyMessage(this.metricOptionsReason);
    },
    canSubmit() {
      if (!this.formValid) return false;
      return !this.conditionalVisibility || !!this.visibilityMetricAttribute;
    },
    hasFormData() {
      if (this.conditionalVisibility || this.visibilityMetricAttribute) return true;
      return METRIC_DATA_KEYS.some((key) => {
        const value = this.form[key];
        return value !== null && value !== undefined && value !== "";
      });
    },
  },
  watch: {
    value(isOpen) {
      this.isOpen = isOpen;
      if (isOpen) this.resetForm();
    },
  },
  methods: {
    // Deliberately leaves formValid alone: each form owns its own validity rule
    // and re-emits it, so seeding a guess here would fight the child.
    resetForm() {
      // Reopening the sheet must not inherit the errors the last attempt painted.
      this.$nextTick(() => this.$refs.metricForm?.resetValidation());
      if (!this.column) {
        this.view = "picker";
        this.form = emptyMetricColumn();
        this.conditionalVisibility = false;
        this.visibilityMetricAttribute = null;
        return;
      }
      // An existing column already carries its type, so skip the picker.
      this.view = "configure";
      this.form = { ...emptyMetricColumn(this.column.componentType), ...this.column };
      this.conditionalVisibility = !!this.column.visibilityMetricAttribute;
      this.visibilityMetricAttribute = this.column.visibilityMetricAttribute ?? null;
    },
    selectType(type) {
      // Switching types drops the previous type's values; re-picking the same
      // type after using the back chevron keeps them.
      if (type !== this.componentType) this.form = emptyMetricColumn(type);
      this.view = "configure";
    },
    handleBack() {
      if (this.view === "configure") {
        this.view = "picker";
        return;
      }
      this.requestClose();
    },
    // Outside click / swipe: the drawer has already closed itself internally, so
    // mirror that in `isOpen` and let the confirm decide whether it comes back.
    handleDrawerInput(next) {
      if (next === this.isOpen) return;
      if (!next && this.value) {
        this.requestClose();
        return;
      }
      this.isOpen = next;
    },
    requestClose() {
      if (this.hasFormData) {
        this.isOpen = false;
        this.showDiscardConfirm = true;
        return;
      }
      this.close();
    },
    handleKeepEditing() {
      this.isOpen = true;
    },
    handleSubmit() {
      // The button stays enabled while the form is incomplete so this click can
      // surface the per-field errors instead of doing nothing.
      if (!this.$refs.metricForm.validate() || !this.canSubmit) return;
      this.$emit("submit", {
        ...this.form,
        visibilityMetricAttribute: this.conditionalVisibility
          ? this.visibilityMetricAttribute
          : null,
      });
      this.close();
    },
    close() {
      this.isOpen = false;
      this.$emit("input", false);
    },
  },
};
</script>

<style scoped>
.sheet-body {
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px;
}

.type-options ::v-deep .v-input--radio-group__input {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.type-option {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  cursor: pointer;
}

.type-option ::v-deep .v-radio {
  flex: 1 1 0;
  min-width: 0;
}

.type-option__preview {
  display: flex;
  flex: 0 0 162px;
  flex-direction: column;
  justify-content: center;
  min-height: 62px;
  padding: 8px;
  border: 1px dashed var(--v-grey-base);
}

.type-option__status {
  display: flex;
  align-items: center;
  gap: 8px;
}

.fields {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.visibility {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.visibility__field {
  padding-left: 60px;
}

.actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
}
</style>
